import React, { useRef, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API, Auth } from "aws-amplify";
import ReactTooltip from "react-tooltip";
import AlertModal from "./AlertModal";
import CustomModal from "_shared/components/Modal/Modal";
import { useZustandStoreTemp } from "_shared/utils/zustandStoreTemp";

function TwitterAndArrows({ basic }) {
  const messagesRef = useRef();
  const [loading, setLoading] = useState(true);
  const [arrows, setArrows] = useState([]);
  const [user, setUser] = useState({});
  const [anonymous, setAnonymous] = useState(false);
  const heightOfWidgets = 580;
  const open = useZustandStoreTemp((state) => state.v2Open);
  const setOpen = useZustandStoreTemp((state) => state.setV2Open);

  const queryClient = useQueryClient();
  const { data: usersOnline } = useQuery({
    queryKey: ["usersOnline"],
    queryFn: async () =>
      (await API.get("auth", "/cognito/usersOnline", {}))?.users,
    refetchInterval: 60000, //1 minute
  });

  const { data: messages } = useQuery({
    queryKey: ["chat"],
    queryFn: async () =>
      (
        await API.graphql({
          query: `{
              chat(limit: 100, offset: 0, order_by: {created_at: asc}) {
                id
                created_at
                updated_at
                content
                username
                user_id
              }
            }`,
        })
      )?.data?.chat.reverse(),
    refetchInterval: 1500, //1.5 seconds
  });

  useEffect(() => {
    const async = async () => {
      if (window.location.pathname.includes("herb-greenberg")) {
        const script1 = document.createElement("script");
        script1.innerHTML = `
          window.SubstackFeedWidget = {
            substackUrl: "herbgreenberg.substack.com",
            posts: 12,
            layout: "center",
            colors: {
              primary: "#FFFFFF",
              secondary: "#BBBBBB",
              background: "#000000",
            }
          };`;
        document.getElementById("root").appendChild(script1);
        const script2 = document.createElement("script");
        script2.setAttribute("src", "https://substackapi.com/embeds/feed.js");
        document.getElementById("root").appendChild(script2);
      }
      try {
        const data = await (
          await fetch("https://mylongbow.com/app-arrows/")
        ).json();
        setArrows(
          data.map((obj) => ({
            ...obj,
            title: obj.title.replaceAll("8230", "?"),
          })),
        );
      } catch (err) {}
      setTimeout(() => {
        setLoading(false);
      }, 1500);
      const currentUser = await Auth.currentAuthenticatedUser({
        bypassCache: false,
      });
      setUser(currentUser?.attributes);
    };
    async();
    ReactTooltip.rebuild();
  }, []);

  useEffect(() => {
    messagesRef.current.scrollTo(0, 0);
  }, [messages?.length]);

  const onSubmit = async (e) => {
    console.log(user);
    e.preventDefault();

    const { input } = Object.fromEntries(new FormData(e.currentTarget));
    e.target.reset();

    await API.graphql({
      query: `mutation($content: String, $username: String) {
        insert_chat_one(object: {content: $content, username: $username}) {
          id
        }
      }`,
      variables: {
        content: input.slice(0, 500),
        username: anonymous
          ? "Anonymous"
          : `${user?.["custom:firstName"]} ${user?.["custom:lastName"]}`.slice(
              0,
              20,
            ),
      },
    });

    await queryClient.refetchQueries(["chat"]);

    setTimeout(() => {
      messagesRef.current.scrollTo(0, 0);
    }, 0);
  };

  const URL_REGEX =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
  const renderText = (txt) =>
    txt.split(" ").map((part) =>
      URL_REGEX.test(part) ? (
        <a
          target="_blank"
          style={{ color: "#59af26", wordBreak: "break-word" }}
          href={part}
        >
          {part}{" "}
        </a>
      ) : (
        part + " "
      ),
    );

  return (
    <>
      <div
        style={{
          display: basic ? "flex" : "none",
          flexDirection: "row",
          width: 805,
          transition: "opacity 1s",
          opacity: loading ? 0 : 1,
        }}
      >
        <div
          style={{
            width: "50%",
            height: heightOfWidgets,
            border: "1px solid #2e2e2e",
            borderRadius: 10,
            color: "#fff",
          }}
        >
          <div
            style={{
              borderBottom: "1px solid #2e2e2e",
              padding: 10,
              height: 50,
              fontSize: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  color: "#fff",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  fontWeight: 700,
                }}
              >
                Chat
              </div>

              <div
                data-tip={`Welcome to Longbow Chat, a space where you can message other users. Your full name serves as your username (as set in your profile). Messages are capped at 500 characters. Only the most recent 100 messages are loaded.`}
                style={{
                  border: "1px solid #999",
                  borderRadius: "100%",
                  width: 20,
                  height: 20,
                  textAlign: "center",
                  fontSize: 13,
                  color: "#999",
                  cursor: "pointer",
                }}
              >
                ?
              </div>
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#999",
              }}
            >
              {user?.profile === "admin" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 5,
                  }}
                >
                  <div>{usersOnline ?? 0} users online</div>
                  <div
                    style={{
                      height: 13,
                      width: 13,
                      backgroundColor: "#59af26",
                      borderRadius: "100%",
                      marginTop: -2,
                    }}
                  />
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 5,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setAnonymous(!anonymous);
                }}
              >
                <div style={{ marginTop: 2 }}>Chat anonymously</div>
                <input checked={anonymous} type="checkbox" />
              </div>
            </div>
          </div>
          <div
            style={{
              width: "100%",
              height: 628,
              overflowY: "scroll",
              overflowX: "clip",
              width: "calc(100% + 10px)",
            }}
          >
            <div
              ref={messagesRef}
              style={{
                height: "calc(100% - 150px)",
                borderRadius: 5,
                overflowY: "scroll",
                color: "#fff",
                padding: 8,
                fontSize: 14,
              }}
            >
              {messages?.map((obj) => (
                <div style={{ margin: "12px 0px" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      fontSize: 11,
                      margin: "0px 5px",
                    }}
                  >
                    <div>{`${obj?.username}`}</div>
                    <div style={{ color: "#999" }}>
                      {new Date(obj?.created_at)
                        ?.toLocaleString("en-US", {
                          month: "numeric",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                        })
                        ?.replace(" AM", "am")
                        ?.replace(" PM", "pm")}
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor:
                        obj?.user_id !== user?.sub ? "#444" : "#4c80f2",
                      padding: "2px 6px",
                      borderRadius: 8,
                      whiteSpace: "break-spaces",
                    }}
                  >
                    {renderText(obj.content)}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={onSubmit}>
              <input
                disabled={loading}
                name="input"
                placeholder="Type a message..."
                style={{
                  height: 50,
                  fontSize: 14,
                  backgroundColor: "#000",
                  color: "#fff",
                  padding: 10,
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: 5,
                }}
              />
            </form>
          </div>
        </div>
        {window.location.pathname.includes("herb-greenberg") ? (
          <div
            style={{
              width: "50%",
              height: heightOfWidgets,
              border: "1px solid #2e2e2e",
              borderRadius: 10,
              color: "#fff",
            }}
          >
            <div
              style={{
                borderBottom: "1px solid #2e2e2e",
                padding: 10,
                height: 50,
                fontSize: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <a
                href="https://herbgreenberg.substack.com/"
                target="_blank"
                rel="noreferrer"
                style={{
                  textDecorationLine: "none",
                  color: "#fff",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 7,
                  marginLeft: 2,
                  marginTop: 2,
                }}
              >
                <img style={{ width: 40, height: 40 }} src="/herb_tabs.png" />
                On the Street
                <span
                  style={{
                    fontSize: 14,
                    color: "#ccc",
                    marginTop: 11,
                  }}
                >
                  with Herb Greenberg
                </span>
              </a>
            </div>
            <div
              style={{
                width: "100%",
                height: heightOfWidgets - 53,
                overflowY: "scroll",
                overflowX: "clip",
                whiteSpace: "pre-line",
                borderRadius: 10,
              }}
            >
              <div id="substack-feed-embed" />
            </div>
          </div>
        ) : (
          <div
            style={{
              width: "50%",
              height: heightOfWidgets,
              border: "1px solid #2e2e2e",
              borderRadius: 10,
              color: "#fff",
            }}
          >
            <div
              style={{
                borderBottom: "1px solid #2e2e2e",
                padding: 10,
                height: 50,
                fontSize: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <a
                href="https://longbow.pro"
                target="_blank"
                rel="noreferrer"
                style={{
                  textDecorationLine: "none",
                  color: "#fff",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  fontWeight: 700,
                }}
              >
                Longbow Arrows
              </a>
            </div>
            <div
              style={{
                width: "100%",
                height: heightOfWidgets - 50,
                overflowY: "scroll",
                overflowX: "clip",
              }}
            >
              {arrows.map((arrow) => {
                return (
                  <div
                    style={{
                      borderBottom: "1px solid #2e2e2e",
                      padding: 10,
                      marginBottom: 10,
                    }}
                  >
                    <a
                      href={"https://longbow.pro"}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecorationLine: "none" }}
                    >
                      <img
                        alt=""
                        style={{
                          width: "100%",
                          height: 200,
                          objectFit: "cover",
                          borderRadius: 10,
                        }}
                        src={arrow.image}
                      />
                      <div style={{ fontSize: 12, color: "#ccc" }}>
                        {arrow.date}
                      </div>
                      <div
                        style={{
                          fontSize: 20,
                          color: "#fff",
                          borderBottom: "1px solid #444",
                        }}
                      >
                        {arrow.title}
                      </div>
                    </a>
                    <div
                      style={{
                        fontSize: 14,
                        color: "#fff",
                        marginTop: 10,
                        whiteSpace: "normal",
                      }}
                    >
                      {arrow.content}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {user.sub && (
          <CustomModal
            title="Announcing Longbow Pro"
            open={open}
            setOpen={setOpen}
            onClose={() => {
              setOpen(false);
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                We've officially launched our newest dashboard, Longbow Pro!
                We're introducing new features such as:
              </div>
              <ul style={{ margin: "auto" }}>
                <li>Unlimited access to our Stock Assist AI assistant</li>
                <li>Unlimited tickers, risk ranges, and alerts</li>
                <li>
                  Fully interactive social media feed with the latest Longbow
                  Arrows
                </li>
              </ul>
              <img
                style={{ width: 600, margin: "auto" }}
                src="https://longbow.pro/marketing1.jpg"
              />
              <a
                target="_blank"
                href={`https://longbow.pro/migrate?id=${user.sub}`}
                style={{
                  background: "green",
                  color: "white",
                  textDecorationLine: "none",
                  borderRadius: 10,
                  padding: "5px 10px",
                  textAlign: "center",
                }}
              >
                Migrate now
              </a>
              <div
                onClick={() => {
                  setOpen(false);
                }}
                style={{ textAlign: "center", cursor: "pointer" }}
              >
                Maybe later
              </div>
            </div>
          </CustomModal>
        )}
      </div>
    </>
  );
}
export default React.memo(TwitterAndArrows);
